import { PermissionScope } from '../../../../api/types.gen';

/*
 * This file defines enums and helper functions for permission levels used in our table.
 *
 * By assigning numeric values to permissions (e.g., None = 0, FullDetails = 1), we ensure that sorting
 * is based on numeric order rather than lexicographic (alphanumeric) order.
 *
 * For folder permissions, a bitwise enum is used. Each permission (Visible, Contact, Owner) is assigned
 * a unique power of two. This allows us to combine multiple permissions using bitwise OR operations.
 * The resulting numeric value uniquely represents a combination of permissions, while still supporting numerical sorting.
 *
 * This approach improves consistency and performance in sorting and filtering operations for permission-based tables.
 */

export enum ReadPermissionLevel {
  None = 0,
  FullDetails = 1,
}

export enum CreatePermissionLevel {
  None = 0,
  CreateItems = 1,
  CreateSubFolders = 2,
  CreateItemsAndSubFolders = 3,
}

export enum EditPermissionLevel {
  None = 0,
  Own = 1,
  All = 2,
}

export enum FolderPermissionLevel {
  None = 0,
  Visible = 1,
  Contact = 2,
  Owner = 4,
}

export function getReadPermissionLevel(canRead: boolean): ReadPermissionLevel {
  return canRead ? ReadPermissionLevel.FullDetails : ReadPermissionLevel.None;
}

export function getCreatePermissionLevel(canCreateItems: boolean, canCreateSubFolders: boolean): CreatePermissionLevel {
  let permissionLevel = CreatePermissionLevel.None;
  if (canCreateItems) {
    permissionLevel |= CreatePermissionLevel.CreateItems;
  }
  if (canCreateSubFolders) {
    permissionLevel |= CreatePermissionLevel.CreateSubFolders;
  }
  return permissionLevel;
}

export function getEditPermissionLevel(editItems?: PermissionScope): EditPermissionLevel {
  switch (editItems) {
    case PermissionScope.OWNED:
      return EditPermissionLevel.Own;
    case PermissionScope.ALL:
      return EditPermissionLevel.All;
    default:
      return EditPermissionLevel.None;
  }
}

export function getFolderPermissionLevel(isOwner: boolean, isVisible: boolean, isContact: boolean): FolderPermissionLevel {
  let permissionLevel = FolderPermissionLevel.None;
  if (isVisible) {
    permissionLevel |= FolderPermissionLevel.Visible;
  }
  if (isContact) {
    permissionLevel |= FolderPermissionLevel.Contact;
  }
  if (isOwner) {
    permissionLevel |= FolderPermissionLevel.Owner;
  }
  return permissionLevel;
}
